<template>
  <form novalidate @submit.prevent="submit">
    <c-box
      :mt="{ base: 'md', md: '4xl' }"
      :mb="{ base: 'md', md: '3rem' }"
      :px="{ base: '0', md: '4xl' }"
      font-size="md"
      data-cy="salesreturn_selected_order_info"
    >
      <c-flex mb="xs">
        <c-text mr="xs" :min-width="{ md: '12rem' }" font-weight="bold">
          {{ $t("common.orderNumber") }}
        </c-text>
        <c-text>{{ form.orderNumber }}</c-text>
      </c-flex>
      <c-flex mb="xs">
        <c-text mr="xs" :min-width="{ md: '12rem' }" font-weight="bold">
          {{ $t("ordering.deliveryDetails.customerReference") }}
        </c-text>
        <c-text>{{ form.orderReference || "-" }}</c-text>
      </c-flex>
      <c-flex mb="xs">
        <c-text mr="xs" :min-width="{ md: '12rem' }" font-weight="bold">
          {{ $t("ordering.timestamp") }}
        </c-text>
        <c-text>{{ form.timestamp || "-" }}</c-text>
      </c-flex>
    </c-box>

    <c-box
      :box-shadow="{ md: 'base' }"
      :border-radius="{ md: 'base' }"
      bg="white"
      :px="{ base: 'md', md: '4xl' }"
      :pt="{ base: 'xl', md: '2.5rem' }"
      :pb="{ base: '2xl', md: '3rem' }"
      :mx="{ base: '-15px', md: '0' }"
      :mb="{ base: 'md', md: '3xl' }"
    >
      <c-heading as="h3" font-size="lg" font-weight="bold" :mb="{ base: 'md', md: '3xl' }">
        {{ $t("productReturn.salesReturn.chooseProductsToReturn") }}
      </c-heading>

      <c-grid
        v-if="proFormaRequired"
        :d="{ base: 'none', md: 'grid' }"
        template-columns="1fr 2fr 2fr 3fr 1fr 2fr"
        :gap="{ md: 'md' }"
        font-weight="bold"
        pb="md"
        border-bottom="1px solid"
        border-bottom-color="gray.300"
      >
        <c-text />
        <c-text :title="$t('productReturn.salesReturn.productSKUs')">
          {{ $t("productReturn.salesReturn.productSKUs") }}
        </c-text>
        <c-text :title="$t('productReturn.salesReturn.productName')">
          {{ $t("productReturn.salesReturn.productName") }}
        </c-text>
        <c-text :title="$t('productReturn.common.customsInformation.categoryTitle')">
          {{ $t("productReturn.common.customsInformation.categoryTitle") }}
        </c-text>
        <c-text :title="$t('productReturn.salesReturn.orderQuantity')">
          {{ $t("productReturn.salesReturn.orderQuantity") }}
        </c-text>
        <c-text :title="$t('productReturn.salesReturn.returnQuantity')">
          {{ $t("productReturn.salesReturn.returnQuantity") }}
        </c-text>
      </c-grid>
      <c-grid
        v-else
        :d="{ base: 'none', md: 'grid' }"
        template-columns="1fr 3fr 4fr 1fr 2fr"
        :gap="{ md: 'md' }"
        font-weight="bold"
        pb="md"
        border-bottom="1px solid"
        border-bottom-color="gray.300"
      >
        <c-text />
        <c-text :title="$t('productReturn.salesReturn.productSKUs')">
          {{ $t("productReturn.salesReturn.productSKUs") }}
        </c-text>
        <c-text :title="$t('productReturn.salesReturn.productName')">
          {{ $t("productReturn.salesReturn.productName") }}
        </c-text>
        <c-text :title="$t('productReturn.salesReturn.orderQuantity')">
          {{ $t("productReturn.salesReturn.orderQuantity") }}
        </c-text>
        <c-text :title="$t('productReturn.salesReturn.returnQuantity')">
          {{ $t("productReturn.salesReturn.returnQuantity") }}
        </c-text>
      </c-grid>

      <c-box
        v-for="i in form.productCount"
        :key="'RMA_PE_' + i"
        border-bottom="1px solid"
        border-bottom-color="gray.300"
      >
        <RMASRProductsEditor
          v-model="form.products[i - 1]"
          :index="i"
          :count="form.productCount"
          :pro-forma-required="proFormaRequired"
          @onRemove="onRemove(i)"
          @errors="onErrors($event)"
          @onCountChanged="checkForProductsToReturn()"
        />
      </c-box>
      <c-box v-if="isErrorField('hasProductsToReturn')" color="brand.red" font-size="sm" my="xs">
        {{ $t("productReturn.salesReturn.noProductsSelected") }}
      </c-box>
    </c-box>

    <c-box
      :box-shadow="{ md: 'base' }"
      :border-radius="{ md: 'base' }"
      bg="white"
      :px="{ base: 'md', md: '4xl' }"
      :pt="{ base: 'xl', md: '2.5rem' }"
      :pb="{ base: '2xl', md: '3rem' }"
      :mx="{ base: '-15px', md: '0' }"
      :mb="{ base: 'md', md: '3xl' }"
    >
      <c-form-control is-required :is-invalid="inputIsInvalid('returnReason')">
        <c-box
          display="flex"
          :flex-direction="{ base: 'column', sm: 'row' }"
          align-items="baseline"
        >
          <c-form-label :min-width="{ xl: '21rem' }" for="returnReason">
            {{ $t("productReturn.salesReturn.returnReason") }}
          </c-form-label>
          <c-box flex="1" width="100%">
            <c-select
              id="returnReason"
              v-model="form.returnReason"
              data-cy="returnReason"
              :is-required="true"
              @change="onFieldChange('returnReason')"
              @blur="onFieldBlur('returnReason')"
              size="lg"
            >
              <option value="" disabled selected hidden>
                {{ $t("productReturn.salesReturn.selectReasonForReturn") }}
              </option>
              <option value="orderedIncorrectProduct">
                {{ $t("productReturn.salesReturn.returnReasons.orderedIncorrectProduct") }}
              </option>
              <option value="receivedIncorrectProduct">
                {{ $t("productReturn.salesReturn.returnReasons.receivedIncorrectProduct") }}
              </option>
              <option value="wrongQuantity">
                {{ $t("productReturn.salesReturn.returnReasons.receivedIncorrectQuantity") }}
              </option>
              <option value="damagedDuringShipping">
                {{ $t("productReturn.salesReturn.returnReasons.damaged") }}
              </option>
              <option value="notNeeded">
                {{ $t("productReturn.salesReturn.returnReasons.notNeeded") }}
              </option>
              <option value="doesNotMatchDescription">
                {{ $t("productReturn.salesReturn.returnReasons.productDoesntMatchDescription") }}
              </option>
              <option value="doesNotMeetExpectations">
                {{ $t("productReturn.salesReturn.returnReasons.productDoesntMeetExpectations") }}
              </option>
              <option value="other">
                {{ $t("productReturn.salesReturn.returnReasons.other") }}
              </option>
            </c-select>

            <c-box v-if="isErrorField('returnReason')" color="brand.red" font-size="sm" my="xs">
              {{
                $t("common.error.fieldRequired", {
                  field: $t("productReturn.salesReturn.returnReason"),
                })
              }}
            </c-box>
          </c-box>
        </c-box>
      </c-form-control>

      <c-form-control :is-invalid="inputIsInvalid('additionalDetails')">
        <c-box
          display="flex"
          :flex-direction="{ base: 'column', sm: 'row' }"
          align-items="baseline"
        >
          <c-form-label :min-width="{ xl: '21rem' }" for="additionalDetails">
            {{ $t("productReturn.salesReturn.additionalDetails") }}
          </c-form-label>
          <c-box flex="1" width="100%">
            <c-textarea
              id="additionalDetails"
              v-model="form.additionalDetails"
              :maxlength="$v.form['additionalDetails'].$params.maxLength.max"
              @blur="onFieldBlur('additionalDetails')"
              size="lg"
              mb="md"
            />
            <c-text font-size="xs" mb="md">
              {{
                $tc("common.form.charactersLeft", getCharactersLeft("additionalDetails"), {
                  charCount: getCharactersLeft("additionalDetails"),
                })
              }}
            </c-text>
            <c-text>
              {{ $t("productReturnV2.salesReturn.noteAboutMakingNewOrders") }}
            </c-text>
            <c-box
              v-if="isErrorField('additionalDetails')"
              color="brand.red"
              font-size="sm"
              my="xs"
            >
              {{
                $t("common.error.fieldTooLong", {
                  field: $t("productReturn.salesReturn.additionalDetails"),
                })
              }}
            </c-box>
          </c-box>
        </c-box>
      </c-form-control>
      <c-form-control :is-invalid="inputIsInvalid('compensationReference')">
        <c-box
          display="flex"
          :flex-direction="{ base: 'column', sm: 'row' }"
          align-items="baseline"
        >
          <c-form-label :min-width="{ xl: '21rem' }" for="compensationReference">
            {{ $t("productReturn.salesReturn.compensationReference") }}
          </c-form-label>
          <c-box flex="1" width="100%">
            <c-input
              id="compensationReference"
              v-model.trim="form.compensationReference"
              :class="getFieldClasses('compensationReference')"
              :placeholder="$t('productReturn.salesReturn.compensationReference')"
              @keyup="onFieldChange('compensationReference')"
              @blur="onFieldBlur('compensationReference')"
              size="lg"
            />
            <c-box
              v-if="isErrorField('compensationReference')"
              color="brand.red"
              font-size="sm"
              my="xs"
            >
              {{
                $t("common.error.fieldInvalid", {
                  field: $t("productReturn.salesReturn.compensationReference"),
                })
              }}
            </c-box>
          </c-box>
        </c-box>
      </c-form-control>
    </c-box>
  </form>
</template>

<script lang="ts">
import { FormValidationMixin } from "@/components/FormValidationMixin.js";
import {
  CBox,
  CFlex,
  CFormControl,
  CFormLabel,
  CGrid,
  CHeading,
  CInput,
  CSelect,
  CText,
  CTextarea,
} from "@chakra-ui/vue";
import { defineComponent } from "vue";
import { maxLength, required } from "vuelidate/lib/validators";
import { inputMixin } from "./RMAMixins";
import RMASRProductsEditor from "./RMASRProductsEditor.vue";

export default defineComponent({
  name: "RMA02SRProducts",
  components: {
    RMASRProductsEditor,
    CBox,
    CText,
    CFlex,
    CHeading,
    CGrid,
    CSelect,
    CFormControl,
    CFormLabel,
    CTextarea,
    CInput,
  },
  mixins: [inputMixin, FormValidationMixin],
  validations: {
    form: {
      orderReference: {},
      returnReason: { required },
      additionalDetails: { maxLength: maxLength(2000) },
      compensationReference: {},
    },
    hasProductsToReturn: { isValid: (v: any) => v === true },
  },
  props: {
    proFormaRequired: Boolean,
  },
  data() {
    return {
      form: {
        productCount: 1,
        products: [],
        returnReason: null,
        additionalDetails: "",
      },
      hasProductsToReturn: false,
    };
  },
  methods: {
    checkForProductsToReturn() {
      this.hasProductsToReturn = this.form.products.some((product: any) => {
        return product?.returnCount && product?.returnCount > 0;
      });
      this.onFieldChange("hasProductsToReturn");
    },
    onRemove(index: number) {
      this.form.productCount -= 1;
      this.form.products.splice(index - 1, 1);
    },
    onErrors(ev: boolean) {
      this.$emit("errors", ev);
    },
    submit(): void {
      // Do nothing
    },
  },
});
</script>
